import { JSX, useContext, useEffect, useMemo } from "react";
import { BulkProjectTypeEnum, ColumnConfigurationReportSection, IncludedReports, TerminationsWizardStepProps } from "@app/terminations/import/interfaces";
import { TerminationsWizardContext } from "@app/terminations/import/TerminationsWizardContext";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { useBulkTerminationModel } from "@app/terminations/import/useBulkTerminationModel";
import ExportService from "@app/exports/exportService";
import PageSection from "@app/shared/PageSection";
import { defaultAllColumns } from "@app/report-customization/build-reports/defaultPresetValue";
import { ColumnConfigurationSection } from "@app/report-customization/build-reports/components/ColumnConfigurationSection";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

const exportService = new ExportService();
// all of the key "as const" bits basically tell typescript these values are readonly
// there's nothing stopping them from actually changing, but TS will throw an error if you do it explicitly
// it effectively changes the type of the key property in a given (array of) object(s) from "any string" to "only this/these particular string(s)"
const getReportSections = (isDistinct: boolean, includedReports: IncludedReports, projectType: BulkProjectTypeEnum, isZipTerminationEnabled: boolean): ColumnConfigurationReportSection[] => {
  // Zip projects have no county summary data, so the Service Area Changed Gaps / Specialty Summary Comparison
  // reports route through the zip "Specialty Summary by Zip Code" columns instead of the county columns, and use
  // the zip report types to keep zip presets separate from county presets.
  // projectType alone can resolve to Zip while ZIP_CODE_ANALYSIS_TERMINATION is disabled (it's gated only on
  // ZIP_CODE_ANALYSIS_VOLUME_TERMINATION in resolveTerminationsProjectType), so require both here to match the
  // server-side gate on the zip column endpoints.
  const isZip = projectType === BulkProjectTypeEnum.Zip && isZipTerminationEnabled;
  return [
    {
      columnsEndpoint: () => exportService.getAdequacyVolumeTermProjectDetailsColumns(projectType),
      key: "projectDetailsTerminationOverview" as const,
      reportType: ReportType.AdequacyBulkTerminationVolumeTermProjectDetails,
      title: "Project Details Termination Overview"
    },
    {
      columnsEndpoint: isZip ? exportService.getAdequacyBulkTerminationZipServiceAreaChangedGapsColumns : exportService.getAdequacyBulkTerminationChangedGapsColumns,
      key: "serviceAreaSpecialtySummaryChangedGaps" as const,
      reportType: isZip ? ReportType.ZipAdequacyBulkTerminationChangedGaps : ReportType.AdequacyBulkTerminationChangedGaps,
      title: "Service Area Specialty Summary Changed Gaps"
    },
    {
      columnsEndpoint: isZip ? exportService.getAdequacyBulkTerminationZipServiceAreaSpecialtySummaryComparisonColumns : exportService.getAdequacyBulkTerminationSpecialtySummaryComparisonsColumns,
      key: "serviceAreaSpecialtySummaryComparison" as const,
      reportType: isZip ? ReportType.ZipAdequacyBulkTerminationSpecialtySummaryComparison : ReportType.AdequacyBulkTerminationSpecialtySummaryComparison,
      title: "Service Area Specialty Summary Comparison"
    },
    {
      columnsEndpoint: exportService.getAdequacyBulkTerminationChangedGapsColumns,
      key: "opportunitySpecialtySummaryChangedGaps" as const,
      reportType: ReportType.AdequacyBulkTerminationChangedGaps,
      title: "Opportunity Specialty Summary Changed Gaps"
    },
    {
      columnsEndpoint: exportService.getAdequacyBulkTerminationSpecialtySummaryComparisonsColumns,
      key: "opportunitySpecialtySummaryComparison" as const,
      reportType: ReportType.AdequacyBulkTerminationSpecialtySummaryComparison,
      title: "Opportunity Specialty Summary Comparison"
    },
    {
      columnsEndpoint: () => (isDistinct ? exportService.getAdequacyBulkTerminationDistinctProvidersColumns(projectType) : exportService.getAdequacyBulkTerminationProvidersColumns(projectType)),
      key: "serviceAreaProviders" as const,
      reportType: isDistinct ? ReportType.AdequacyBulkTerminationDistinctProviders : ReportType.AdequacyBulkTerminationProviders,
      title: isDistinct ? "Distinct Service Area Providers" : "Service Area Providers",
    },
    {
      columnsEndpoint: () => (isDistinct ? exportService.getAdequacyBulkTerminationDistinctProvidersColumns(projectType) : exportService.getAdequacyBulkTerminationProvidersColumns(projectType)),
      key: "opportunityProviders" as const,
      reportType: isDistinct ? ReportType.AdequacyBulkTerminationDistinctProviders : ReportType.AdequacyBulkTerminationProviders,
      title: isDistinct ? "Distinct Opportunity Providers" : "Opportunity Providers",
    },
  ].filter((s) => includedReports[s.key]);
}

const TerminationsWizardColumnConfiguration = (props: TerminationsWizardStepProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const isZipTerminationEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);
  const reportSections = useMemo(() => { return getReportSections(wizardContext.isDistinctServicingProviderReport, wizardContext.includedReports, wizardContext.projectType, isZipTerminationEnabled) }, [wizardContext.isDistinctServicingProviderReport, wizardContext.includedReports, wizardContext.projectType, isZipTerminationEnabled]);
  const model = useBulkTerminationModel();

  useEffect(() => {
    props.formIsValid(true);
  }, []);

  useEffect(() => {
    // if the presets have already been loaded, and every selected report type has presets, don't request them again
    // for example, clicking previous and next in the wizard without changing anything
    const presetValues = Object.values(wizardContext.reportPresets);
    const includedReportTypes = [...new Set(reportSections.map((r) => r.reportType))];
    if (presetValues.length > 0
      && includedReportTypes.every((i) => wizardContext.reportPresets[i]?.length > 0)
      && presetValues.every((r) => r.length > 0) && includedReportTypes.every((i) => wizardContext.reportPresets[i]?.length > 0)
    ) {
      return;
    }

    Promise.all(
      reportSections.map(({ reportType }) =>
        exportService.getColumnCustomizationPresets(reportType).then((presets) => ({ presets, reportType }))
      )
    ).then((results) => {
      const newReportPresets: Partial<Record<ReportType, ColumnConfigurationPresetOption[]>> = {};
      results.forEach(({ reportType, presets }) => {
        newReportPresets[reportType] = [defaultAllColumns, ...presets];
      });

      wizardContext.setAllReportPresets(newReportPresets);
    });
  }, []);

  return (
    <PageSection>
      {reportSections.length === 0 ? (
        <h3 className="text-muted no-content-text" data-testid="terminations-wizard-column-configuration__no-reports-message">No configurable reports selected.</h3>
      ) : (
        <div className="container">
          {reportSections.map((section) => {
            const presets = wizardContext.reportPresets[section.reportType] ?? [];
            const selectedPreset = wizardContext.selectedReportPreset[section.key];

            const handlePresetsRefresh = async () => {
              const updatedPresets = [defaultAllColumns, ...await exportService.getColumnCustomizationPresets(section.reportType)];
              wizardContext.setReportPresets(section.reportType, updatedPresets);
            }

            return (
              <div key={section.key} className="mb-4">
                <div className="terminations-wizard__card-title">{section.title}</div>
                <div className="px-3 mt-2">
                  <ColumnConfigurationSection
                    columnsEndpoint={section.columnsEndpoint}
                    exportModel={model}
                    presets={presets}
                    selectedPreset={selectedPreset}
                    reportType={section.reportType}
                    onPresetChange={(newOption) => { wizardContext.setSelectedReportPreset(section.key, newOption); }}
                    onPresetsRefresh={handlePresetsRefresh}
                    hideHeading={true}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageSection>
  );
};

export default TerminationsWizardColumnConfiguration;
